/**
 * Feature Flags Management Page
 */

import { useState } from 'react';
import { Plus, AlertCircle, History, Users, Flag } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  FlagList,
  FlagEditor,
  UserOverridesPanel,
  AuditLogPanel,
  useFeatureFlags,
  useToggleFlag,
  useUpdateFlags,
} from '@/features/feature-flags';
import { useI18n } from '@/i18n';
import type { FeatureFlag } from '@/types/featureFlags';

type TabType = 'flags' | 'overrides' | 'audit';

export function FeatureFlagsPage() {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState<TabType>('flags');
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingFlag, setEditingFlag] = useState<{
    name: string;
    config: FeatureFlag;
  } | null>(null);

  const { data, isLoading, error } = useFeatureFlags();
  const toggleMutation = useToggleFlag();
  const updateMutation = useUpdateFlags();

  const flags = data?.flags ?? {};
  const flagEntries = Object.entries(flags);

  const handleToggle = async (flagName: string, enabled: boolean) => {
    await toggleMutation.mutateAsync({ flagName, enabled });
  };

  const handleEdit = (flagName: string) => {
    setEditingFlag({ name: flagName, config: flags[flagName] });
    setIsEditorOpen(true);
  };

  const handleCreate = () => {
    setEditingFlag(null);
    setIsEditorOpen(true);
  };

  const handleSave = async (
    flagName: string,
    flagConfig: FeatureFlag,
    isNew: boolean
  ) => {
    const updatedFlags = { ...flags, [flagName]: flagConfig };
    await updateMutation.mutateAsync({
      flags: updatedFlags,
      changeReason: isNew ? `Created flag: ${flagName}` : `Updated flag: ${flagName}`,
    });
    setIsEditorOpen(false);
    setEditingFlag(null);
  };

  const handleDelete = async (flagName: string) => {
    const { [flagName]: removed, ...remaining } = flags;
    await updateMutation.mutateAsync({
      flags: remaining,
      changeReason: `Deleted flag: ${flagName}`,
    });
  };

  const tabs = [
    { id: 'flags' as const, label: t('featureFlags.tabs.flags'), icon: Flag },
    { id: 'overrides' as const, label: t('featureFlags.tabs.userOverrides'), icon: Users },
    { id: 'audit' as const, label: t('featureFlags.tabs.auditLog'), icon: History },
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <Flag className="w-8 h-8 text-primary-600" />
            <h1 className="text-2xl font-bold text-gray-900">{t('featureFlags.pageTitle')}</h1>
          </div>
          <Button variant="primary" onClick={handleCreate}>
            <Plus className="w-4 h-4 mr-2" />
            {t('featureFlags.createFlag')}
          </Button>
        </div>
        <p className="text-gray-600">
          {t('featureFlags.pageDescription')}
          {data && (
            <span className="ml-2 text-sm text-gray-500">
              {t('featureFlags.version')}: {data.version}
            </span>
          )}
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex gap-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex items-center gap-2 px-4 py-3 border-b-2 transition-colors
                ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-700 font-medium'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }
              `}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div>
              <h3 className="text-red-800 font-semibold">{t('featureFlags.errors.loadingError')}</h3>
              <p className="text-red-600 text-sm">{(error as Error).message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        </div>
      )}

      {/* Tab Content */}
      {!isLoading && !error && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          {activeTab === 'flags' && (
            <FlagList
              flags={flagEntries}
              onToggle={handleToggle}
              onEdit={handleEdit}
              onDelete={handleDelete}
              isToggling={toggleMutation.isPending}
            />
          )}
          {activeTab === 'overrides' && (
            <UserOverridesPanel flagNames={Object.keys(flags)} />
          )}
          {activeTab === 'audit' && <AuditLogPanel />}
        </div>
      )}

      {/* Editor Dialog */}
      {isEditorOpen && (
        <FlagEditor
          flagName={editingFlag?.name}
          flagConfig={editingFlag?.config}
          existingFlags={Object.keys(flags)}
          onSave={handleSave}
          onClose={() => {
            setIsEditorOpen(false);
            setEditingFlag(null);
          }}
          isSubmitting={updateMutation.isPending}
        />
      )}
    </div>
  );
}
